import React, { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Download,
  FileText,
  Table2,
  ChevronLeft,
  X,
  Check,
  Minus,
  List,
  CheckCheck,
} from "lucide-react";
import PdfPreviewModal from "../ui/PdfPreviewModal";
import { useToast } from "../../contexts/NotificationContext";

// ── Helpers ───────────────────────────────────────────────────────────────

function buildSel(schema, value) {
  const s = {};
  schema.sections.forEach((sec) => {
    s[sec.id] = {};
    sec.champs.forEach((c) => {
      s[sec.id][c.cle] = value;
    });
  });
  return s;
}

function countSel(schema, sel) {
  return schema.sections.reduce(
    (acc, sec) => acc + sec.champs.filter((c) => sel[sec.id]?.[c.cle]).length,
    0,
  );
}

function totalChamps(schema) {
  return schema.sections.reduce((acc, sec) => acc + sec.champs.length, 0);
}

function etatSection(sec, sel) {
  const n = sec.champs.filter((c) => sel[sec.id]?.[c.cle]).length;
  if (n === 0) return "aucun";
  if (n === sec.champs.length) return "tout";
  return "partiel";
}

function getPrimaryRgb() {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--color-primary")
    .trim();
  if (!raw) return [20, 184, 166];
  return raw.split(" ").map(Number);
}

// ── ChoiceCard ─────────────────────────────────────────────────────────────

function ChoiceCard({ icon: Icon, title, desc, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 flex flex-col items-start gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left group relative"
    >
      <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
        <Icon size={18} />
      </div>
      <div className="font-semibold text-sm text-gray-900">{title}</div>
      <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
      <div className="absolute top-3 right-3 w-4 h-4 rounded-full border-2 border-gray-300 group-hover:border-primary flex items-center justify-center transition-colors">
        <Check
          size={10}
          className="text-primary opacity-0 group-hover:opacity-100"
        />
      </div>
    </button>
  );
}

// ── ScopeModal ─────────────────────────────────────────────────────────────

function ScopeModal({ schema, onPickAll, onPickSelect, onClose }) {
  const total = totalChamps(schema);
  const nSec = schema.sections.length;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 pt-5 pb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Exporter les données
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Choisissez ce que vous souhaitez exporter de la fiche.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 pb-4">
          <div className="flex gap-3">
            <ChoiceCard
              icon={CheckCheck}
              title="Tout exporter"
              desc={`Exporter l'ensemble des données visibles sur cette fiche (${total} champs sur ${nSec} sections).`}
              onClick={onPickAll}
            />
            <ChoiceCard
              icon={List}
              title="Sélection manuelle"
              desc="Choisir précisément les sections et les champs à inclure dans l'export."
              onClick={onPickSelect}
            />
          </div>
        </div>
        <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}

// ── FormatModal ────────────────────────────────────────────────────────────

function FormatModal({ count, onPickPdf, onPickCsv, onBack, onClose }) {
  const [pick, setPick] = useState("pdf");
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 pt-5 pb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Format d'exportation
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {count} champ{count > 1 ? "s" : ""} sélectionné
              {count > 1 ? "s" : ""} — choisissez le format de sortie.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 pb-4">
          <div className="flex gap-3">
            <button
              onClick={() => setPick("pdf")}
              className={`flex-1 flex flex-col items-start gap-2 p-4 border-2 rounded-xl text-left transition-all ${
                pick === "pdf"
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
                <FileText size={18} />
              </div>
              <div className="font-semibold text-sm text-gray-900">
                Document PDF
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                Mise en page imprimable, ouverte dans le visualiseur intégré
                avec téléchargement.
              </p>
            </button>
            <button
              onClick={() => setPick("csv")}
              className={`flex-1 flex flex-col items-start gap-2 p-4 border-2 rounded-xl text-left transition-all ${
                pick === "csv"
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                <Table2 size={18} />
              </div>
              <div className="font-semibold text-sm text-gray-900">
                Fichier CSV
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                Données tabulaires brutes, prêtes à être ouvertes dans Excel ou
                un tableur.
              </p>
            </button>
          </div>
        </div>
        <div className="px-6 py-3 border-t border-gray-100 flex items-center gap-2">
          <button
            onClick={onBack}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft size={14} /> Retour
          </button>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            Annuler
          </button>
          <button
            onClick={() => (pick === "pdf" ? onPickPdf() : onPickCsv())}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Download size={13} /> Générer{" "}
            {pick === "pdf" ? "le PDF" : "le CSV"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CsvPreviewModal ────────────────────────────────────────────────────────

function CsvPreviewModal({ schema, sel, nomFichier, onClose }) {
  const entetes = [];
  const valeurs = [];
  schema.sections.forEach((sec) => {
    sec.champs.forEach((c) => {
      if (!sel[sec.id]?.[c.cle]) return;
      entetes.push(`${sec.titre} — ${c.label}`);
      valeurs.push((c.valeur || "").replace(/"/g, '""'));
    });
  });

  const handleDownload = () => {
    const ligneEntetes = entetes.map((h) => `"${h}"`).join(";");
    const ligneValeurs = valeurs.map((v) => `"${v}"`).join(";");
    const csv = ligneEntetes + "\n" + ligneValeurs;
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${nomFichier}_export.csv`;
    a.click();
    URL.revokeObjectURL(url);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-800 rounded-t-xl">
          <Table2 size={15} className="text-green-400 flex-shrink-0" />
          <span className="text-sm font-medium text-white">
            {nomFichier}_export.csv
          </span>
          <span className="text-xs text-gray-400">
            — {entetes.length} colonne{entetes.length > 1 ? "s" : ""} · 1 ligne
          </span>
          <div className="flex-1" />
          <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Download size={13} /> Télécharger
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-5 bg-gray-100 rounded-b-xl overflow-auto">
          <div className="bg-gray-900 rounded-lg p-4 font-mono text-xs overflow-auto max-h-64">
            <div className="text-green-400 mb-1.5 break-all">
              {entetes.map((h) => `"${h}"`).join(";")}
            </div>
            <div className="text-gray-100 break-all">
              {valeurs.map((v) => `"${v}"`).join(";")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SelectionModal ─────────────────────────────────────────────────────────

function SelectionModal({ schema, sel, setSel, onValidate, onCancel }) {
  const total = totalChamps(schema);
  const count = countSel(schema, sel);

  const toggleChamp = (secId, cle) => {
    setSel((prev) => ({
      ...prev,
      [secId]: { ...prev[secId], [cle]: !prev[secId]?.[cle] },
    }));
  };

  const toggleSection = useCallback(
    (sec) => {
      const target = etatSection(sec, sel) !== "tout";
      setSel((prev) => {
        const next = { ...prev, [sec.id]: { ...prev[sec.id] } };
        sec.champs.forEach((c) => { next[sec.id][c.cle] = target; });
        return next;
      });
    },
    [sel, setSel],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Sélection des données</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Cochez les champs à inclure dans l'export.
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable sections */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {schema.sections.map((sec) => {
            const etat = etatSection(sec, sel);
            const allChecked = etat === "tout";
            const someChecked = etat === "partiel";
            return (
              <div
                key={sec.id}
                className={`rounded-xl border transition-all ${
                  allChecked ? "border-primary ring-2 ring-primary/20" : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-xl">
                  <span className="flex-1 text-sm font-semibold text-gray-800">{sec.titre}</span>
                  <button
                    onClick={() => toggleSection(sec)}
                    className="text-xs text-primary hover:text-primary-dark font-medium"
                  >
                    {allChecked ? "Tout décocher" : "Tout cocher"}
                  </button>
                  <div
                    onClick={() => toggleSection(sec)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${
                      allChecked
                        ? "bg-primary border-primary"
                        : someChecked
                          ? "bg-primary/10 border-primary"
                          : "bg-white border-gray-300"
                    }`}
                  >
                    {allChecked && <Check size={12} className="text-white" strokeWidth={3} />}
                    {someChecked && <Minus size={12} className="text-primary" strokeWidth={3} />}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-px bg-gray-100 rounded-b-xl overflow-hidden">
                  {sec.champs.map((c) => {
                    const checked = !!sel[sec.id]?.[c.cle];
                    return (
                      <div
                        key={c.cle}
                        onClick={() => toggleChamp(sec.id, c.cle)}
                        className={`flex items-start gap-2.5 px-4 py-3 cursor-pointer transition-colors ${
                          checked ? "bg-primary/5" : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <div
                          className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            checked ? "bg-primary border-primary" : "bg-white border-gray-300"
                          }`}
                        >
                          {checked && <Check size={10} className="text-white" strokeWidth={3} />}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs text-gray-500">{c.label}</div>
                          <div className="text-sm text-gray-900 truncate">{c.valeur || "—"}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center gap-3 px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
            {count}
          </div>
          <span className="text-sm text-gray-500 flex-1">
            {count === 0 ? "Aucun champ sélectionné" : `${count} champ${count > 1 ? "s" : ""} sur ${total}`}
          </span>
          <button
            onClick={() => setSel(buildSel(schema, true))}
            className="text-xs text-gray-500 hover:text-gray-800 transition-colors"
          >
            Tout cocher
          </button>
          <button
            onClick={() => setSel(buildSel(schema, false))}
            className="text-xs text-gray-500 hover:text-gray-800 transition-colors"
          >
            Tout décocher
          </button>
          <div className="w-px h-4 bg-gray-300" />
          <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900">
            Annuler
          </button>
          <button
            onClick={onValidate}
            disabled={count === 0}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Check size={13} /> Valider
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PDF generation ─────────────────────────────────────────────────────────

async function genererPdf(schema, sel, structureName) {
  const { jsPDF } = await import("jspdf");
  const { applyPlugin } = await import("jspdf-autotable");
  applyPlugin(jsPDF);

  const [r, g, b] = getPrimaryRgb();
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  const now = new Date();
  const dateStr = now.toLocaleDateString("fr-FR");
  const ref = `EXP-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;

  // Header band
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, pageW, 20, "F");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("SuiviPlus", margin, 8);
  if (structureName) {
    doc.setFont("helvetica", "normal");
    doc.text(structureName, pageW / 2, 8, { align: "center" });
  }
  doc.setFont("helvetica", "normal");
  doc.text(`Document généré le ${dateStr}`, pageW - margin, 8, {
    align: "right",
  });
  doc.text(`Réf. ${ref}`, pageW - margin, 14, { align: "right" });

  // Title
  let y = 32;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42);
  doc.text(schema.titre, margin, y);
  y += 7;

  if (schema.sousTitre) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(schema.sousTitre, margin, y);
    y += 7;
  }

  const nb = countSel(schema, sel);
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  doc.text(
    `${nb} champ${nb > 1 ? "s" : ""} exporté${nb > 1 ? "s" : ""}`,
    margin,
    y,
  );
  y += 9;

  // Sections
  for (const sec of schema.sections) {
    const coches = sec.champs.filter((c) => sel[sec.id]?.[c.cle]);
    if (coches.length === 0) continue;

    // Section title bar
    doc.setFillColor(236, 253, 248);
    doc.rect(margin, y - 4, pageW - 2 * margin, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(r, g, b);
    doc.text(sec.titre.toUpperCase(), margin + 2, y);
    y += 7;

    // 2-column field table
    const colW = (pageW - 2 * margin) / 2;
    const body = [];
    for (let i = 0; i < coches.length; i += 2) {
      const c1 = coches[i];
      const c2 = coches[i + 1];
      body.push([
        c1.label,
        c1.valeur || "—",
        c2 ? c2.label : "",
        c2 ? c2.valeur || "—" : "",
      ]);
    }

    doc.autoTable({
      startY: y,
      head: [],
      body,
      margin: { left: margin, right: margin },
      tableWidth: pageW - 2 * margin,
      columnStyles: {
        0: {
          cellWidth: colW * 0.4,
          textColor: [100, 116, 139],
          fontSize: 8,
          fontStyle: "normal",
        },
        1: {
          cellWidth: colW * 0.6,
          textColor: [15, 23, 42],
          fontSize: 9,
          fontStyle: "bold",
        },
        2: {
          cellWidth: colW * 0.4,
          textColor: [100, 116, 139],
          fontSize: 8,
          fontStyle: "normal",
        },
        3: {
          cellWidth: colW * 0.6,
          textColor: [15, 23, 42],
          fontSize: 9,
          fontStyle: "bold",
        },
      },
      styles: {
        cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
      },
      theme: "plain",
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Footer
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(margin, pageH - 14, pageW - margin, pageH - 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text("SuiviPlus · Module RH", margin, pageH - 9);
  doc.text("Page 1 / 1", pageW - margin, pageH - 9, { align: "right" });

  return doc;
}

// ── ExportButton ────────────────────────────────────────────────────────────

export default function ExportButton({ schema, structureName, disabled }) {
  const toast = useToast();
  const [phase, setPhase] = useState("idle");
  const [portee, setPortee] = useState(null);
  const [sel, setSel] = useState({});
  const [pdfDoc, setPdfDoc] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const goIdle = useCallback(() => setPhase("idle"), []);

  const pickAll = () => {
    setSel(buildSel(schema, true));
    setPortee("tout");
    setPhase("format");
  };

  const pickSelect = () => {
    setSel(buildSel(schema, false));
    setPortee("manuel");
    setPhase("select");
  };

  const validerSelection = () => {
    if (countSel(schema, sel) === 0) return;
    setPhase("format");
  };

  const annulerSelection = () => setPhase("idle");

  const retour = () => {
    setPhase(portee === "tout" ? "scope" : "select");
  };

  const lancerPdf = async () => {
    setPhase("idle");
    setGeneratingPdf(true);
    try {
      const doc = await genererPdf(schema, sel, structureName);
      setPdfDoc(doc);
      setPhase("pdf");
    } catch {
      toast.error("Impossible de générer le PDF");
    }
    setGeneratingPdf(false);
  };

  const lancerCsv = () => setPhase("csv");

  if (!schema || schema.sections.length === 0) return null;

  return (
    <>
      <button
        onClick={() => setPhase("scope")}
        disabled={disabled || generatingPdf}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-primary text-white rounded-lg font-medium hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {generatingPdf ? (
          <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
        ) : (
          <Download size={13} />
        )}
        Exporter
      </button>

      {phase === "scope" &&
        createPortal(
          <ScopeModal
            schema={schema}
            onPickAll={pickAll}
            onPickSelect={pickSelect}
            onClose={goIdle}
          />,
          document.body,
        )}

      {phase === "select" &&
        createPortal(
          <SelectionModal
            schema={schema}
            sel={sel}
            setSel={setSel}
            onValidate={validerSelection}
            onCancel={annulerSelection}
          />,
          document.body,
        )}

      {phase === "format" &&
        createPortal(
          <FormatModal
            count={countSel(schema, sel)}
            onPickPdf={lancerPdf}
            onPickCsv={lancerCsv}
            onBack={retour}
            onClose={goIdle}
          />,
          document.body,
        )}

      {phase === "pdf" &&
        pdfDoc &&
        createPortal(
          <PdfPreviewModal
            doc={pdfDoc}
            fileName={`${schema.nomFichier}_export.pdf`}
            onClose={() => {
              setPdfDoc(null);
              goIdle();
            }}
          />,
          document.body,
        )}

      {phase === "csv" &&
        createPortal(
          <CsvPreviewModal
            schema={schema}
            sel={sel}
            nomFichier={schema.nomFichier}
            onClose={goIdle}
          />,
          document.body,
        )}
    </>
  );
}
